import {
    inject,
    Injectable,
    makeStateKey,
    PLATFORM_ID,
    TransferState,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, tap } from 'rxjs';
import { AppsManagementResponse } from '../../types/common.type';
import { environment } from '@environment/environment';
import { ItemPage } from '../../types/page.type';
import { isPlatformServer } from '@angular/common';
import { definitions } from '@services/cms';
import { ValueGlobalService } from '@services/global/value.global.service';

@Injectable({ providedIn: 'root' })
export class PageService {
    private http = inject(HttpClient);
    private transferState = inject(TransferState);
    private platformId = inject<Object>(PLATFORM_ID);

    readonly #valueGlobalService = inject(ValueGlobalService);
    readonly localeDefaultService = this.#valueGlobalService.locale;

    getPage(slug: string): Observable<AppsManagementResponse<ItemPage>> {
        const STATE_KEY_PAGE = makeStateKey<AppsManagementResponse<ItemPage>>(
            `page-${slug}`
        );
        let url = `${environment.clientUrl}/${slug}`;

        if (this.transferState.hasKey(STATE_KEY_PAGE)) {
            const savedData = this.transferState.get(STATE_KEY_PAGE, null);
            this.transferState.remove(STATE_KEY_PAGE); // Remove after retrieving
            return new Observable((observer) => {
                observer.next(savedData);
                observer.complete();
            });
        } else {
            return this.http.get<AppsManagementResponse<ItemPage>>(url).pipe(
                tap((data) => {
                    if (isPlatformServer(this.platformId)) {
                        this.transferState.set(STATE_KEY_PAGE, data);
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching page:', error);
                    return new Observable<AppsManagementResponse<ItemPage>>(
                        (observer) => {
                            observer.next({
                                message: 'Error fetching page',
                                data: null,
                                status: 0,
                            });
                            observer.complete();
                        }
                    );
                })
            );
        }
    }

    getPages(
        slug: string
    ): Observable<definitions['v_api.Response-do_PageDO']> {
        const STATE_KEY_PAGE = makeStateKey<
            definitions['v_api.Response-do_PageDO']
        >(`page-${slug}-${this.localeDefaultService()?.code}`);
        let url = `${environment.clientUrl}/${slug}`;

        if (this.transferState.hasKey(STATE_KEY_PAGE)) {
            const savedData = this.transferState.get(STATE_KEY_PAGE, null);
            this.transferState.remove(STATE_KEY_PAGE);
            return new Observable((observer) => {
                observer.next(savedData);
                observer.complete();
            });
        } else {
            return this.http
                .get<definitions['v_api.Response-do_PageDO']>(url)
                .pipe(
                    tap((data) => {
                        if (isPlatformServer(this.platformId)) {
                            this.transferState.set(STATE_KEY_PAGE, data);
                        }
                    }),
                    catchError((error) => {
                        console.error('Error fetching page:', error);
                        return new Observable<
                            definitions['v_api.Response-do_PageDO']
                        >((observer) => {
                            observer.next({
                                message: 'Error fetching page',
                                data: null,
                            });
                            observer.complete();
                        });
                    })
                );
        }
    }
}
